import { Component, Suspense, createElement, type ComponentType, type ReactNode } from "react";

/**
 * Code splitting (audit S-12). Heavy modules that are not needed to see or close a position (the
 * chart, the Pine / NinjaScript tools, settings, copy trader, alerts, journal, screener) load as
 * separate chunks. The order entry, DOM ladder, positions tables, banners, connection logic and
 * every Flatten / Exit path stay in the entry chunk, so trading never waits on a chunk download.
 *
 * Every lazy component renders inside its own `ChunkBoundary`: a chunk that fails to load (or a
 * module that throws while rendering) shows a small "Retry" box in place of that module only; it
 * never unmounts the shell around it. All chunks are precached by the service worker
 * (vite.config.ts `globPatterns`), and `preloadLazyChunks()` warms them once the app is idle.
 */

export type LazyComponent<P> = ComponentType<P> & { preload: () => Promise<void> };

const preloaders: (() => Promise<void>)[] = [];

type Resource<T> =
  | { status: "pending"; promise: Promise<void> }
  | { status: "fulfilled"; value: T }
  | { status: "rejected"; reason: unknown };

/**
 * A component loaded on first render. A rejection stays readable by the boundary until the
 * user presses Retry; clearing it in the rejection handler can cause an endless Suspense loop.
 */
export function lazyComponent<P extends object>(
  load: () => Promise<ComponentType<P>>,
  label: string,
): LazyComponent<P> {
  let resource: Resource<ComponentType<P>> | null = null;
  const start = (): Resource<ComponentType<P>> => {
    if (resource) return resource;
    const promise = Promise.resolve()
      .then(load)
      .then(
        (c) => {
          resource = { status: "fulfilled", value: c };
        },
        (e: unknown) => {
          resource = { status: "rejected", reason: e };
        },
      );
    resource = { status: "pending", promise };
    return resource;
  };
  function Inner(props: P): ReactNode {
    const r = start();
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Suspense consumes the pending promise.
    if (r.status === "pending") throw r.promise;
    if (r.status === "rejected") throw r.reason;
    return createElement(r.value, props);
  }
  function Lazy(props: P): React.JSX.Element {
    return (
      <ChunkBoundary
        label={label}
        onRetry={() => {
          if (resource?.status === "rejected") resource = null;
        }}
      >
        {createElement(Inner, props)}
      </ChunkBoundary>
    );
  }
  Lazy.displayName = `Lazy(${label})`;
  const preload = (): Promise<void> => {
    const r = start();
    return r.status === "pending" ? r.promise : Promise.resolve();
  };
  preloaders.push(preload);
  return Object.assign(Lazy, { preload });
}

/** Load every lazy chunk in the background (idempotent; failures offer Retry on first render). */
export function preloadLazyChunks(): Promise<void> {
  return Promise.all(preloaders.map((p) => p())).then(() => undefined);
}

type BoundaryProps = { label: string; children: ReactNode; onRetry?: () => void };
type BoundaryState = { error: unknown; attempt: number };

/**
 * Suspense + error boundary for one lazily loaded module. The fallback is deliberately tiny and
 * local; a failure offers Retry and says that trading controls are unaffected.
 */
export class ChunkBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null, attempt: 0 };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error: error ?? new Error("Module could not be loaded") };
  }

  override componentDidCatch(error: unknown): void {
    console.error(`[lazy] ${this.props.label} failed`, error);
  }

  override render(): ReactNode {
    const { label, children } = this.props;
    if (this.state.error !== null) {
      return (
        <div className="lazy-error" role="alert" data-lazy-error={label}>
          <span>Couldn&apos;t load {label}. Order entry, positions and Flatten are unaffected.</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              this.props.onRetry?.();
              this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));
            }}
          >
            Retry
          </button>
          {this.state.attempt > 0 && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload app
            </button>
          )}
        </div>
      );
    }
    return (
      <Suspense
        key={this.state.attempt}
        fallback={
          <div className="lazy-fallback" role="status" aria-live="polite" data-lazy-loading={label}>
            Loading {label}…
          </div>
        }
      >
        {children}
      </Suspense>
    );
  }
}
